import React from 'react';
import { useParams } from 'react-router-dom-v5-compat';
import PropTypes from 'prop-types';
import {
  VaCard,
  VaLink,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { buildDateFormatter } from '~/platform/shared/claims-appeals';
import recordEvent from 'platform/monitoring/record-event';
import BasicLink from '../web-component-wrappers/BasicLink';
import { claimTabLinkProps } from '../../utils/claim-tab-helpers';

const formatDate = buildDateFormatter();

/**
 * Anchor id placed on the heading of the first applicant card with repeat
 * ineligibility letter activity. ChampvaRepeatIneligibilityAlert's
 * "Read updated decision" link targets this so the user lands on the card the
 * alert is talking about. Lives here because this component owns the markup
 * that carries the id.
 */
export const REPEAT_INELIGIBILITY_ANCHOR_ID =
  'champva-repeat-ineligibility-card';

/**
 * Determine the display tag for a given VES eligibility status string.
 * Returns null if the status is null/undefined (card should be hidden).
 * Checks INELIGIBLE before ELIGIBLE to avoid the substring overlap.
 */
function getEligibilityTag(vesEligibilityStatus) {
  if (!vesEligibilityStatus) return null;
  const upper = vesEligibilityStatus.toUpperCase();
  if (upper.includes('INELIGIBLE'))
    return { status: 'info', text: 'NOT ENROLLED' };
  if (upper.includes('ELIGIBLE'))
    return { status: 'success', text: 'ENROLLED' };
  return null;
}

function splitReasonAndMailedSentence(reasonText) {
  if (!reasonText) return null;

  const mailedPhrase = 'We mailed ';
  const mailedIndex = reasonText.indexOf(mailedPhrase);

  if (mailedIndex === -1) {
    return {
      reasonLine: reasonText,
      mailedLine: null,
    };
  }

  const reasonLine = reasonText.slice(0, mailedIndex).trimEnd();
  const mailedLine = reasonText.slice(mailedIndex).trimStart();

  return { reasonLine, mailedLine };
}

/**
 * Guidebook paragraph + link, shown only on ENROLLED cards (#154551). Sits on its own line
 * beneath the welcome-letter sentence, per the approved design.
 *
 * Copy and URL come from claimStatusMeta.enrolledCard rather than being hardcoded, so the
 * guidebook can be re-pointed without a frontend deploy -- the published URL carries the
 * edition in its path (.../2026-03/...) and changes whenever the guidebook is revised.
 * Renders nothing when the section is absent, the same way renderReasonLink bails on an
 * incomplete link: staging and production cache this config in Redis for 30 minutes, so a
 * frontend deploy can briefly run against config that predates the section.
 */
function renderGuidebookSection(enrolledCard) {
  const text = enrolledCard?.guidebookText;
  const linkText = enrolledCard?.guidebookLinkText;
  const linkUrl = enrolledCard?.guidebookLinkUrl;

  if (!text || !linkText || !linkUrl) {
    return null;
  }

  return (
    <>
      <p
        data-testid="champva-guidebook-text"
        className="vads-u-margin-top--2 vads-u-margin-bottom--1"
      >
        {text}
      </p>
      <p className="vads-u-margin-top--0 vads-u-margin-bottom--0">
        <VaLink
          data-testid="champva-guidebook-link"
          href={linkUrl}
          text={linkText}
        />
      </p>
    </>
  );
}

/**
 * Reason-link paragraph (currently only the two BE reasons that carry one:
 * "tricare eligible" and "sponsor doad eligibility"). Shared by both NOT ENROLLED
 * paths so repeat letter activity never drops a link the initial decision showed.
 */
function renderReasonLink(vesEligibilityReasonLink) {
  if (!vesEligibilityReasonLink?.url || !vesEligibilityReasonLink?.text) {
    return null;
  }

  return (
    <p
      data-testid="champva-reason-link-paragraph"
      className="vads-u-margin-top--2 vads-u-margin-bottom--1"
    >
      <VaLink
        data-testid="champva-reason-link"
        href={vesEligibilityReasonLink.url}
        text={vesEligibilityReasonLink.text}
      />
    </p>
  );
}

/**
 * Upload paragraph + link, shown on any NOT ENROLLED card when the BE reports
 * documentsRequested. Shared so the copy and link props stay in step across the
 * initial-decision and repeat-ineligibility paths.
 */
function renderUploadSection(documentsRequested, displayName, uploadLinkProps) {
  if (!documentsRequested) return null;

  return (
    <>
      <p
        data-testid="champva-card-upload-paragraph"
        className="vads-u-margin-top--2 vads-u-margin-bottom--1"
      >
        If {displayName} received a letter asking for supporting documents, you
        can upload them using this tool.
      </p>
      <BasicLink
        data-testid="champva-upload-link"
        {...uploadLinkProps}
        disable-analytics
        onClick={event => {
          recordEvent({
            event: 'nav-link-click',
            'link-label': 'Upload supporting documents',
            'link-destination': event.currentTarget.getAttribute('href'),
            'link-origin': undefined,
          });
        }}
        text={`Upload supporting documents for ${displayName}`}
      />
    </>
  );
}

/**
 * Renders the body content for a single eligibility card.
 *
 * ENROLLED: static welcome-letter copy (reason map value is ignored for eligible applicants).
 * NOT ENROLLED + repeat ineligibility letter: updated card with "Update:" prefix,
 *   existing reason, and new letter sentence — driven by hasRepeatIneligibilityLetter flag
 *   and repeatIneligibilityCard config from claimStatusMeta. The dated new-letter sentence
 *   replaces the BE reason's own "We mailed" sentence so only one renders; with no letter
 *   date the original sentence is kept instead.
 * NOT ENROLLED + mapped reason: render BE reason text while inserting spacing
 *   between the reason sentence and a trailing "We mailed" sentence when present.
 * NOT ENROLLED + null reason: FE fallback copy that includes mailed-letter text.
 * Upload paragraph + link: shown for any NOT ENROLLED card when documentsRequested is true.
 */
function renderCardBody(
  applicant,
  uploadLinkProps,
  repeatIneligibilityCard,
  enrolledCard,
) {
  const {
    firstName,
    vesEligibilityStatus,
    vesEligibilityReason,
    vesEligibilityReasonLink,
    documentsRequested,
    hasRepeatIneligibilityLetter,
    repeatIneligibilityLetterDate,
  } = applicant;

  const upper = (vesEligibilityStatus || '').toUpperCase();
  const isEnrolled =
    upper.includes('ELIGIBLE') && !upper.includes('INELIGIBLE');

  // Single source for the nameless-applicant fallback used throughout this card.
  const displayName = firstName || 'this applicant';

  const fallbackReason = `${
    firstName || 'This applicant'
  }'s not eligible for CHAMPVA benefits. We mailed ${displayName} a letter that has more information about our decision. It should arrive within 10 business days.`;

  const reasonContent = splitReasonAndMailedSentence(
    vesEligibilityReason || fallbackReason,
  );

  if (isEnrolled) {
    return (
      <>
        <p
          data-testid="champva-card-body-enrolled"
          className="vads-u-margin-top--2 vads-u-margin-bottom--0"
        >
          We mailed {displayName} a welcome letter and a CHAMPVA ID card. It
          should arrive within 10 business days.
        </p>
        {renderGuidebookSection(enrolledCard)}
      </>
    );
  }

  // NOT ENROLLED + repeat ineligibility letter — show updated card content
  if (hasRepeatIneligibilityLetter && repeatIneligibilityCard) {
    const updatePrefix = (repeatIneligibilityCard.updatePrefix || '').replace(
      '[Name]',
      displayName,
    );
    const newLetterSentence = repeatIneligibilityLetterDate
      ? (repeatIneligibilityCard.newLetterSentence || '')
          .replace('[Name]', displayName)
          .replace('[date]', formatDate(repeatIneligibilityLetterDate))
      : null;
    return (
      <>
        {updatePrefix && (
          <p
            data-testid="champva-card-body-repeat-update-prefix"
            className="vads-u-margin-top--2 vads-u-margin-bottom--1"
          >
            <strong>Update:</strong> {updatePrefix}
          </p>
        )}
        <p
          data-testid="champva-card-body-not-enrolled"
          className="vads-u-margin-top--2 vads-u-margin-bottom--1"
        >
          {reasonContent?.reasonLine}
        </p>
        {renderReasonLink(vesEligibilityReasonLink)}
        {/* The BE reason text keeps its own "We mailed [Name] a letter..." sentence
            (see ClaimBuilder#applicant_data_hash — repeat activity is deliberately not
            stripped from vesEligibilityReason). The new dated letter sentence replaces
            it rather than stacking on top, so only one mailed-letter paragraph ever
            renders. When there's no letter date we fall back to the original sentence
            rather than dropping the mailed-letter context entirely. */}
        {newLetterSentence ? (
          <p
            data-testid="champva-card-body-repeat-new-letter"
            className="vads-u-margin-top--2 vads-u-margin-bottom--1"
          >
            {newLetterSentence}
          </p>
        ) : (
          reasonContent?.mailedLine && (
            <p
              data-testid="champva-card-body-not-enrolled-mailed"
              className="vads-u-margin-top--2 vads-u-margin-bottom--1"
            >
              {reasonContent.mailedLine}
            </p>
          )
        )}
        {renderUploadSection(documentsRequested, displayName, uploadLinkProps)}
      </>
    );
  }

  // NOT ENROLLED
  return (
    <>
      <p
        data-testid="champva-card-body-not-enrolled"
        className="vads-u-margin-top--2 vads-u-margin-bottom--1"
      >
        {reasonContent?.reasonLine}
      </p>
      {renderReasonLink(vesEligibilityReasonLink)}
      {reasonContent?.mailedLine && (
        <p
          data-testid="champva-card-body-not-enrolled-mailed"
          className="vads-u-margin-top--2 vads-u-margin-bottom--1"
        >
          {reasonContent.mailedLine}
        </p>
      )}
      {renderUploadSection(documentsRequested, displayName, uploadLinkProps)}
    </>
  );
}

/**
 * Renders per-applicant eligibility decision cards for CHAMPVA claims.
 *
 * Visibility rules:
 *  - Cards are rendered only for applicants with a recognized VES status (ELIGIBLE* or INELIGIBLE).
 *  - Cards with null, undefined, empty, or unrecognized vesEligibilityStatus are hidden.
 *  - The entire section is hidden until at least one applicant has a recognized status.
 *
 * Gate: rendered only for CHAMPVA claims with cstChampvaCustomContent and
 * ivcChampvaVesEligibilityOnDemand feature flags enabled (enforced in ClaimStatusPage).
 */
export default function ChampvaEligibilityCards({
  applicants,
  applicationDecided,
  repeatIneligibilityCard = null,
  enrolledCard = null,
  useVaTabs = false,
}) {
  const { id: claimId } = useParams();
  const uploadLinkProps = useVaTabs
    ? claimTabLinkProps('files', { claimId, search: '?type=ivc_champva' })
    : { path: '../files', search: '?type=ivc_champva' };
  const decidedApplicants = applicants.filter(
    a => getEligibilityTag(a.vesEligibilityStatus) !== null,
  );

  if (!decidedApplicants.length) return null;

  // The alert's "Read updated decision" link targets the FIRST applicant with
  // repeat letter activity (AC: "or the first affected applicant card when
  // multiple applicants have repeat ineligibility activity"). -1 when none
  // qualify, in which case no card carries the anchor id.
  const firstRepeatIndex = decidedApplicants.findIndex(
    a => a.hasRepeatIneligibilityLetter,
  );

  // isDecided comes from the BE-computed applicationDecided field, not derived on FE.

  return (
    <section
      data-testid="champva-eligibility-cards"
      data-decided={applicationDecided}
      className="vads-u-margin-bottom--4"
      aria-labelledby="champva-eligibility-heading"
    >
      <h3
        id="champva-eligibility-heading"
        className="vads-u-margin-top--0 vads-u-margin-bottom--3"
      >
        Application decision
      </h3>
      {decidedApplicants.map((applicant, index) => {
        const tag = getEligibilityTag(applicant.vesEligibilityStatus);
        const name = `${applicant.firstName || ''} ${
          applicant.lastName || ''
        }`.trim();

        return (
          <VaCard
            key={`${applicant.firstName}-${applicant.lastName}-${index}`}
            data-testid={`champva-eligibility-card-${index}`}
            className="vads-u-margin-bottom--3"
          >
            {/* Anchor sits on the heading rather than the VaCard host so focus
                lands on the applicant's name — a screen reader then announces
                which card the user was moved to. */}
            <h4
              id={
                index === firstRepeatIndex
                  ? REPEAT_INELIGIBILITY_ANCHOR_ID
                  : undefined
              }
              className="vads-u-margin-top--0 vads-u-margin-bottom--2"
            >
              {name}
            </h4>
            <div className="vads-u-margin-bottom--2">
              <va-tag-status status={tag.status} text={tag.text} />
            </div>
            {renderCardBody(
              applicant,
              uploadLinkProps,
              repeatIneligibilityCard,
              enrolledCard,
            )}
            {((applicant.hasRepeatIneligibilityLetter &&
              applicant.repeatIneligibilityLetterDate) ||
              applicant.vesStatusUpdatedDate) && (
              <p className="vads-u-margin-top--2 vads-u-margin-bottom--0">
                {applicant.hasRepeatIneligibilityLetter &&
                applicant.repeatIneligibilityLetterDate
                  ? `Last updated on ${formatDate(
                      applicant.repeatIneligibilityLetterDate,
                    )}`
                  : `Last updated on ${formatDate(
                      applicant.vesStatusUpdatedDate,
                    )}`}
              </p>
            )}
          </VaCard>
        );
      })}
    </section>
  );
}

ChampvaEligibilityCards.propTypes = {
  applicants: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      vesStatusUpdatedDate: PropTypes.string,
      vesEligibilityStatus: PropTypes.string,
      vesEligibilityReason: PropTypes.string,
      vesEligibilityReasonLink: PropTypes.shape({
        text: PropTypes.string,
        url: PropTypes.string,
      }),
      documentsRequested: PropTypes.bool,
      hasRepeatIneligibilityLetter: PropTypes.bool,
      repeatIneligibilityLetterDate: PropTypes.string,
    }),
  ).isRequired,
  applicationDecided: PropTypes.bool,
  enrolledCard: PropTypes.shape({
    guidebookText: PropTypes.string,
    guidebookLinkText: PropTypes.string,
    guidebookLinkUrl: PropTypes.string,
  }),
  repeatIneligibilityCard: PropTypes.shape({
    updatePrefix: PropTypes.string,
    newLetterSentence: PropTypes.string,
  }),
  useVaTabs: PropTypes.bool,
};
